import React, { useEffect, useState } from 'react';

import { Box, Tag } from '@chakra-ui/react';
import { Chip } from '@material-ui/core';
import { postAddTagAction } from 'actions/addPost';
import Carousel from 'components/Carousel';
import { useDispatch, useSelector } from 'react-redux';
import { selectEditorState } from 'selectors/addPost';
import styled, { css } from 'styled-components';

import { SEARCH_WIDGET_TYPES } from 'constants/ProjectConstants';

import { replaceTextOfAllEntities } from 'services/helpers/post';
import { tagAutocomplete } from 'services/search';
import { showErrorSnackbar } from 'services/snackbar';

import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { CollapsableCompetencyRabbit } from './CollapsableCompetencyRabbit';

export const areSameIds = (prevIdArr = [], newIdArr = []) =>
  prevIdArr.every((id) => newIdArr.includes(id)) && newIdArr.every((id) => prevIdArr.includes(id));

const ApplicableCompetenciesSectionRabbit = ({ employeeId }) => {
  const dispatch = useDispatch();
  const editorState = useSelector(selectEditorState);

  const [loading, setLoading] = useState(true);

  const [applicableCompetencies, setApplicableCompetencies] = useState([]);
  const [mentionedCompetencies, setMentionedCompetencies] = useState([]);
  const [mentionedEmployeeIds, setMentionedEmployeeIds] = useState([]);

  const addTag = (tag) => {
    dispatch(postAddTagAction({ ...tag, stats: null, meshTags: null }));
  };

  useEffect(() => {
    if (editorState) {
      const body = {
        macros: {
          employees: {},
          demographics: {},
          attributes: { orgTag: {}, money: null },
        },
      };

      replaceTextOfAllEntities(editorState, body);

      const newMentionedEmployeeIds = Object.keys(body.macros.employees);
      const newMentionedCompetencies = Object.keys(body.macros.attributes.orgTag);

      if (!areSameIds(mentionedEmployeeIds, newMentionedEmployeeIds)) {
        setMentionedEmployeeIds(newMentionedEmployeeIds);
      }

      if (!areSameIds(mentionedCompetencies, newMentionedCompetencies)) {
        setMentionedCompetencies(newMentionedCompetencies);
      }
    }
  }, [editorState]);

  useEffect(() => {
    setLoading(true);
    tagAutocomplete({
      query: '',
      teamId: null,
      widgetType: SEARCH_WIDGET_TYPES.post,
      employeeIds: mentionedEmployeeIds,
    })
      .then((res) => {
        setApplicableCompetencies(res);
      })
      .catch((err) => {
        console.error(err);
        dispatch(showErrorSnackbar(err.message || 'Failed to fetch competency list'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [mentionedEmployeeIds]);

  return (
    <>
      {loading ? (
        <SkeletonLoader />
      ) : (
        <Box p="16px" overflow="auto">
          <CollapsableCompetencyRabbit>
            {applicableCompetencies?.map((tag) => (
              <Tag
                sx={{
                  marginRight: '8px',
                  marginTop: '8px',
                  cursor: 'pointer',
                }}
                key={tag.uuid}
                selected={mentionedCompetencies?.includes(tag.uuid)}
                clickable
                onClick={() => addTag(tag)}
              >
                {tag?.displayName}
              </Tag>
            ))}
          </CollapsableCompetencyRabbit>
        </Box>
      )}
    </>
  );
};

const ContainerCSS = css`
  padding: 0px 24px;
  margin: 24px 0px;
`;

const Container = styled.div`
  ${ContainerCSS};
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
`;

const StyledCarousel = styled(Carousel)`
  ${ContainerCSS};
`;

const CompetencyChip = styled(Chip)`
  height: 24px;
  display: inline-flex;
  background-color: ${(props) =>
    props.disabled ? props.theme.palette.background.lightGrey3 : props.theme.palette.background.lightBlue};
  &:hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
  }
  .MuiChip-label {
    color: ${(props) => (props.disabled ? props.theme.palette.text.disabled : props.theme.palette.primary.main)};
    font-size: 14px;
    line-height: 17.6px;
  }
  ${(props) => (props.disabled ? `pointer-events: none` : ``)};
`;

export default ApplicableCompetenciesSectionRabbit;
